import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { Flex, Text, TextVariant, Modal, useScrollVisible, InfoIcon } from '@mprolab/uikit'
import { BNToNumberWithDecimals } from '@mprolab/sdk'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { logMessage } from 'utils/logMessage'
import { useMproRewardProxyContract } from 'hooks/contracts/useMproRewardProxyContract'
import { useGetRewardActivityQuery } from 'state/services/indexNode/indexNode'
import { useLifetimeTokens } from 'state/global/hooks'
import UseAnimations from 'react-useanimations'
import loadingAnimation from 'react-useanimations/lib/loading'

/**
 * The whole log scrolls, not just the middle list.
 *
 * Only the chronological list used to scroll, while the summary above it and
 * the per-licence section below it sat outside — so on a wallet with any real
 * history the modal grew past the viewport and the parts at the bottom could
 * not be reached at all.
 */
const ContentWrapper = styled(Flex)<{ scrollable: boolean }>`
  flex-direction: column;
  margin-top: 24px;
  width: 560px;
  overflow-y: auto;
  max-height: calc(100vh - 240px);
  padding-right: ${({ scrollable }) => (scrollable ? '12px' : 'unset')};

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
    max-height: calc(100vh - 180px);
  }
`

/** Plain list — the scrolling belongs to ContentWrapper above. */
const List = styled(Flex)`
  flex-direction: column;
  gap: 2px;
`

const Row = styled(Flex)`
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray10};
`

const Summary = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray10};
  margin-bottom: 16px;
`

const SummaryLine = styled(Flex)`
  justify-content: space-between;
  gap: 16px;
`

const SectionTitle = styled(Text)`
  margin: 16px 0 8px;
`

/** One entry in the log. Mints come from our records, claims from the chain. */
interface LogEntry {
  kind: 'mint' | 'claim'
  timestamp: number
  amount: number
  licenses?: { licenseId: string; name: string | null; reward: number }[]
  txHash?: string
  failed?: boolean
}

interface ClaimHistoryModalProps {
  rewardProxyChainId?: number
  onDismiss?: () => void
}

const short = (id: string): string => (id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id)
const day = (ts: number): string => dayjs.unix(ts).format('YYYY-MM-DD HH:mm')

/**
 * One activity log for the wallet: what a licence earned and has not been
 * minted, every mint, every claim, and the fees those mints accrued.
 *
 * The fee part exists because the contract charges in a way nobody could see:
 * each addReward adds `updateRewardNativeFee` to the wallet's balance without
 * taking anything, and the whole accumulated amount falls due at the next
 * claim. Users met a charge they had no way to explain.
 */
const ClaimHistoryModal: React.FC<ClaimHistoryModalProps> = ({ onDismiss, rewardProxyChainId }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const wrapper = useRef<HTMLDivElement>(null)
  const scrollVisible = useScrollVisible(wrapper as MutableRefObject<HTMLDivElement>)
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]
  const { getWalletRewardUpdates, getReward } = useMproRewardProxyContract()

  const [claims, setClaims] = useState<LogEntry[]>([])
  const [onChain, setOnChain] = useState({ pendingReward: 0, accruedFee: 0 })
  const [chainLoading, setChainLoading] = useState(true)

  const { data: activity, isLoading: activityLoading } = useGetRewardActivityQuery(
    { account, accessToken },
    { skip: !account || !accessToken }
  )

  useEffect(() => {
    const fetchChain = async () => {
      try {
        if (!account || !rewardProxyChainId) {
          setClaims([])
          return
        }
        const [history, reward] = await Promise.all([
          getWalletRewardUpdates(account, rewardProxyChainId),
          getReward(account, rewardProxyChainId)
        ])
        setClaims(
          history.map((item) => ({
            kind: 'claim' as const,
            timestamp: item._timestamp.toNumber(),
            amount: BNToNumberWithDecimals(item._rewardAmount, 18, 6)
          }))
        )
        setOnChain({
          pendingReward: BNToNumberWithDecimals(reward._rewardToClaim, 18, 6),
          accruedFee: BNToNumberWithDecimals(reward._claimRewardFee, 18, 6)
        })
      } catch (error) {
        logMessage(`Failed to read reward activity from chain: ${error}`)
      } finally {
        setChainLoading(false)
      }
    }
    void fetchChain()
  }, [account, rewardProxyChainId, getWalletRewardUpdates, getReward])

  const entries = useMemo<LogEntry[]>(() => {
    const mints: LogEntry[] = (activity?.mints ?? []).map((m) => ({
      kind: 'mint' as const,
      timestamp: m.timestamp,
      amount: m.totalReward,
      licenses: m.licenses,
      txHash: m.txHash,
      failed: m.failed
    }))
    return [...mints, ...claims].sort((a, b) => b.timestamp - a.timestamp)
  }, [activity, claims])

  const loading = activityLoading || chainLoading

  return (
    <Modal onDismiss={onDismiss} title={t('rewards.activityLogTitle')}>
      <ContentWrapper ref={wrapper} scrollable={scrollVisible}>
        <Summary>
          <SummaryLine>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('rewards.activityNotMinted')}
            </Text>
            <Text variant={TextVariant.BODY_SMALL_BOLD}>
              {`${(activity?.totalUnclaimed ?? 0).toFixed(5)} MPRO`}
            </Text>
          </SummaryLine>
          <SummaryLine>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('rewards.activityMintedAwaiting')}
            </Text>
            <Text variant={TextVariant.BODY_SMALL_BOLD}>{`${onChain.pendingReward.toFixed(5)} MPRO`}</Text>
          </SummaryLine>
          <SummaryLine>
            <Flex alignItems="center" gap="6px">
              <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                {t('rewards.activityFeeDue')}
              </Text>
              {/* The explanation belongs on hover, not on the page. */}
              <InfoIcon
                iconProps={{ size: 16 }}
                tooltipProps={{ title: t('rewards.activityFeeInfo'), placement: 'top' }}
              />
            </Flex>
            <Text variant={TextVariant.BODY_SMALL_BOLD}>{`${onChain.accruedFee.toFixed(5)} ETH`}</Text>
          </SummaryLine>
        </Summary>

        <Flex alignItems="center" gap="6px">
          <SectionTitle variant={TextVariant.BODY_SMALL_BOLD}>{t('rewards.activityHistory')}</SectionTitle>
          {!!activity?.accrualDataFrom && (
            <InfoIcon
              iconProps={{ size: 16 }}
              tooltipProps={{
                title: t('rewards.activityDataFrom', {
                  date: dayjs.unix(activity.accrualDataFrom).format('YYYY-MM-DD')
                }),
                placement: 'top'
              }}
            />
          )}
        </Flex>
        <List>
          {loading && (
            <Flex justifyContent="center" py="16px">
              <UseAnimations animation={loadingAnimation} strokeColor={colors.gray6} size={32} />
            </Flex>
          )}
          {!loading && entries.length === 0 && (
            <Text textAlign="center" color={colors.gray6}>
              {t('rewards.activityEmpty')}
            </Text>
          )}
          {!loading &&
            entries.map((e) => (
              <Row key={`${e.kind}-${e.timestamp}-${e.txHash ?? ''}`}>
                <Flex flexDirection="column" gap="2px">
                  <Text variant={TextVariant.BODY_SMALL_BOLD}>
                    {e.kind === 'mint'
                      ? e.failed
                        ? t('rewards.activityMintFailed')
                        : t('rewards.activityMint')
                      : t('rewards.activityClaim')}
                  </Text>
                  <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                    {day(e.timestamp)}
                  </Text>
                  {e.licenses?.length ? (
                    <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                      {e.licenses
                        .map((l) => `${l.name ?? short(l.licenseId)}: ${l.reward.toFixed(2)}`)
                        .join(' · ')}
                    </Text>
                  ) : null}
                </Flex>
                <Text variant={TextVariant.BODY_SMALL_BOLD}>{`${e.amount.toFixed(5)} MPRO`}</Text>
              </Row>
            ))}
        </List>

        {!!activity?.unclaimed?.length && (
          <>
            <SectionTitle variant={TextVariant.BODY_SMALL_BOLD}>
              {t('rewards.activityNotMintedPerLicense')}
            </SectionTitle>
            <Flex flexDirection="column" gap="2px">
              {activity.unclaimed.map((u) => (
                <Row key={u.licenseId}>
                  <Flex flexDirection="column" gap="2px">
                    <Text variant={TextVariant.BODY_SMALL_BOLD}>{u.name ?? short(u.licenseId)}</Text>
                    <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                      {u.firstCycleAt && u.lastCycleAt
                        ? `${dayjs.unix(u.firstCycleAt).format('YYYY-MM-DD')} – ${dayjs
                            .unix(u.lastCycleAt)
                            .format('YYYY-MM-DD')} · ${u.rowCount}`
                        : `${u.rowCount}`}
                    </Text>
                  </Flex>
                  <Text variant={TextVariant.BODY_SMALL_BOLD}>{`${u.unclaimedReward.toFixed(5)} MPRO`}</Text>
                </Row>
              ))}
            </Flex>
          </>
        )}

      </ContentWrapper>
    </Modal>
  )
}

export default ClaimHistoryModal
